import {
  getStorage,
  ref as storageRef,
  getDownloadURL,
} from "firebase/storage";

import { getFirestore, collection, doc, getDoc } from "firebase/firestore";
import { useAlertStore } from "@/store/alert";
import { reactive } from "vue";
export function useFbStorageHelpers() {
  const alertStore = useAlertStore();

  const allImgPaths = reactive({});
  const resolveImageEntry = (raw) => {
    if (!raw) {
      return { id: null, url: null, cacheKeys: [] };
    }

    if (typeof raw === "string") {
      if (/^https?:\/\//i.test(raw)) {
        return { id: null, url: raw, cacheKeys: [raw] };
      }
      const segments = raw.split("/").filter(Boolean);
      const id = segments.length ? segments[segments.length - 1] : raw;
      const cacheKeys = id === raw ? [id] : [raw, id];
      return { id, url: null, cacheKeys };
    }

    if (typeof raw === "object") {
      if (typeof raw.downloadURL === "string") {
        const id =
          typeof raw.id === "string"
            ? raw.id
            : typeof raw.path === "string"
              ? raw.path.split("/").filter(Boolean).pop() || null
              : null;
        const cacheKeys = [
          ...(id ? [id] : []),
          raw.downloadURL,
        ];
        return { id, url: raw.downloadURL, cacheKeys };
      }

      if (typeof raw.id === "string" || typeof raw.path === "string") {
        const candidate = typeof raw.path === "string" ? raw.path : raw.id;
        const segments = candidate.split("/").filter(Boolean);
        const id = segments.length ? segments[segments.length - 1] : candidate;
        const cacheKeys = id === candidate ? [id] : [candidate, id];
        return { id, url: null, cacheKeys };
      }
    }

    return { id: null, url: null, cacheKeys: [] };
  };
  const getUrls = async (imgPaths) => {
    // imgPaths is now an array of image ids
    if (!imgPaths || imgPaths.length === 0) return;

    try {
      const db = getFirestore();
      const imagesCollection = collection(db, "images_db");

      // Create a batch of document queries for the image ids
      const promises = imgPaths.map(async (raw) => {
        const { id, url, cacheKeys } = resolveImageEntry(raw);

        if (cacheKeys.length === 0) {
          console.warn("Skipping unresolvable image reference:", raw);
          return;
        }

        if (url) {
          cacheKeys.forEach((cacheKey) => {
            allImgPaths[cacheKey] = url;
          });
          return;
        }

        if (!id) {
          console.warn("Skipping image without resolvable id:", raw);
          return;
        }

        if (cacheKeys.some((cacheKey) => allImgPaths[cacheKey])) {
          return;
        }

        const docRef = doc(imagesCollection, id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const imageData = docSnap.data();
          // Use the downloadURL field from the document
          cacheKeys.forEach((cacheKey) => {
            allImgPaths[cacheKey] = imageData.downloadURL;
          });
        } else {
          console.error(`Image with id ${id} not found`);
        }
      });

      await Promise.all(promises);
    } catch (error) {
      alertStore.showAlert(
        `Failed to fetch some images. Please check their IDs and try again.`,
        "error",
      );
      console.error("Error fetching image documents:", error);
    }
  };
  const getUrl = async (imageId) => {
    // Return early if no imageId provided
    if (!imageId) {
      console.warn("No image ID provided to getUrl");
      return null;
    }

    const { id, url, cacheKeys } = resolveImageEntry(imageId);

    if (url) {
      cacheKeys.forEach((cacheKey) => {
        allImgPaths[cacheKey] = url;
      });
      return url;
    }

    if (!id) {
      console.warn("Unable to resolve image identifier:", imageId);
      return null;
    }

    const cachedKey = cacheKeys.find((key) => allImgPaths[key]);
    if (cachedKey) {
      return allImgPaths[cachedKey];
    }

    try {
      const db = getFirestore();
      const imagesCollection = collection(db, "images_db");

      const docRef = doc(imagesCollection, id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const imageData = docSnap.data();
        const downloadURL = imageData.downloadURL || null;
        if (downloadURL) {
          cacheKeys.forEach((cacheKey) => {
            allImgPaths[cacheKey] = downloadURL;
          });
        } else {
          console.error(`Image document for id ${id} missing downloadURL field`);
        }
        return downloadURL;
      } else {
        console.error(`Image with id ${id} not found`);
        return null;
      }
    } catch (error) {
      console.error(`Error fetching image document for id ${id}:`, error);
      return null;
    }
  };

  return {
    allImgPaths,
    getUrls,
    getUrl,
  };
}
